import { useState, useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/hooks/useAuth";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription } from "@/components/ui/alert-dialog";
import type { PasswordRequirement } from "@/components/find-owner/PasswordPromptDialog";
import { Card, CardContent } from "@/components/ui/card";
import { ShoppingCart } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { AboutHeader } from "@/components/about/AboutHeader";
import { AboutHeroSection } from "@/components/about/AboutHeroSection";
import { AboutFindOwnerSection } from "@/components/about/AboutFindOwnerSection";
import { AboutHowItWorksSection } from "@/components/about/AboutHowItWorksSection";
import { AboutProblemSolutionSection } from "@/components/about/AboutProblemSolutionSection";
import { AboutFeaturesSection } from "@/components/about/AboutFeaturesSection";
import { AboutUseCasesSection } from "@/components/about/AboutUseCasesSection";
import { AboutSocialProofSection } from "@/components/about/AboutSocialProofSection";
import { AboutTeamSection } from "@/components/about/AboutTeamSection";
import { AboutBlogSection } from "@/components/about/AboutBlogSection";
import { AboutCtaSection } from "@/components/about/AboutCtaSection";
import { AboutFaqSection } from "@/components/about/AboutFaqSection";
import { AboutOwnerProfile } from "@/components/about/AboutOwnerProfile";
import { AboutFooter } from "@/components/about/AboutFooter";
import { FloatingAboutDock } from "@/components/about/FloatingAboutDock";
import { ThemeDialog } from "@/components/theme/ThemeDialog";
import { getDemoProfile } from "@/utils/demoProfile";
import { Helmet } from 'react-helmet-async';
import { getFinderLocation } from "@/utils/getLocation";
import { edgeFunctions } from "@/config/edgeFunctions";
import MobileBottomNav from "@/components/dashboard/MobileBottomNav";
import { useQueryClient } from '@tanstack/react-query';

type SearchType = "vehicleNumber" | "metroCard" | "metefyToken" | "username";
type PasswordMode = "disabled" | "last4" | "custom";

interface Profile {
  id: string;
  first_name: string | null;
  last_name: string | null;
  x_username: string | null;
  bio: string | null;
  heading?: string | null;
  site_link?: string | null;
  mask_bio?: boolean;
  metro_card_number: string | null;
  vehicle_number: string | null;
  created_at: string;
  updated_at: string;
  show_email: boolean;
  show_mobile: boolean;
  metefy_token: string | null;
  photo_url?: string | null;
  // NEW
  social_sms?: string | null;
  social_call?: string | null;
  social_telegram?: string | null;
  social_whatsapp?: string | null;
  use_social_icons?: boolean | null;
  metro_password_mode?: PasswordMode;
  metro_card_password?: string | null;
  vehicle_password_mode?: PasswordMode;
  vehicle_password?: string | null;
}

// (Floating social/contact buttons removed for mobile app UX)

export default function Index() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [searchType, setSearchType] = useState<SearchType>("metroCard");
  const [searchValue, setSearchValue] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [searchResult, setSearchResult] = useState<Profile | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isFromUrl, setIsFromUrl] = useState(false);
  const [isScanDemoLoading, setIsScanDemoLoading] = useState(false);
  const [pendingDemoProfile, setPendingDemoProfile] = useState<Profile | null>(null);
  const [isStickerDialogOpen, setIsStickerDialogOpen] = useState(false);
  const [pendingProfile, setPendingProfile] = useState<Profile | null>(null);
  const [passwordRequirement, setPasswordRequirement] = useState<PasswordRequirement | null>(null);
  const [passwordInput, setPasswordInput] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [pendingTokenPassword, setPendingTokenPassword] = useState<string | null>(null);
  const [isPasswordValidating, setIsPasswordValidating] = useState(false);
  const [qrScanToken, setQrScanToken] = useState<string | null>(null);
  const [isThemeDialogOpen, setIsThemeDialogOpen] = useState(false);
  const stickerShowcaseImages = [
    "https://res.cloudinary.com/deqzsmrxq/image/upload/v1760627381/Site_Posts_apdspu.png",
    "https://res.cloudinary.com/deqzsmrxq/image/upload/v1760627368/Site_Posts_2_ast368.png",
    "https://res.cloudinary.com/deqzsmrxq/image/upload/v1760627361/Site_Posts_7_xzu6fl.png",
    "https://res.cloudinary.com/deqzsmrxq/image/upload/v1760627365/Site_Posts_3_ovezi7.png",
    "https://res.cloudinary.com/deqzsmrxq/image/upload/v1760627375/Site_Posts_1_gypuwl.png",
    "https://res.cloudinary.com/deqzsmrxq/image/upload/v1760627365/Site_Posts_8_dgqyme.png",
    "https://res.cloudinary.com/deqzsmrxq/image/upload/v1760627381/Site_Posts_9_twcxeh.png",
    "https://res.cloudinary.com/deqzsmrxq/image/upload/v1760627363/Site_Posts_6_qvodxi.png"
  ];
  const sanitizePassword = (value: string) => value.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
  const getLastFourCharacters = (value?: string | null) => sanitizePassword(value || "").slice(-4);

  const resetPasswordState = () => {
    setPasswordInput("");
    setPasswordError("");
    setPasswordRequirement(null);
    setPendingProfile(null);
    setPendingTokenPassword(null);
    setIsPasswordValidating(false);
  };

  const handlePasswordCancel = () => {
    resetPasswordState();
      setIsDialogOpen(false);
      setSearchResult(null);
      setIsSearching(false);
  };

  const presentProfile = (
    profile: Profile,
    context: "metro" | "vehicle" | "token" | null = null,
    options?: { token?: string; hasPassword?: boolean }
  ) => {
    const needsPassword = context ? requirePasswordIfNeeded(profile, context, options) : false;
    if (!needsPassword) {
      resetPasswordState();
    }
    setSearchResult(profile);
    setIsDialogOpen(true);
  };

  const requirePasswordIfNeeded = (
    profileData: Profile,
    context: "metro" | "vehicle" | "token",
    options?: { token?: string; hasPassword?: boolean }
  ): boolean => {
    if (context === "token") {
      // For token searches, only require password if one is actually set
      const token = options?.token || "";
      const hasPassword = options?.hasPassword ?? false;
      
      if (!token) {
        resetPasswordState();
        return false;
      }
      
      // If no password is set, don't require it
      if (!hasPassword) {
        resetPasswordState();
        return false;
      }
      
      setPendingProfile(profileData);
      setPasswordRequirement({ type: "token", mode: "custom" });
      setPendingTokenPassword(token); // Store token for edge function call
      setPasswordInput("");
      setPasswordError("");
      return true;
    }
    const mode: PasswordMode =
      (context === "metro" ? (profileData as any).metro_password_mode : (profileData as any).vehicle_password_mode) ||
      "disabled";
    if (mode === "disabled") {
      resetPasswordState();
      return false;
    }
    setPendingProfile(profileData);
    setPasswordRequirement({ type: context, mode });
    setPasswordInput("");
    setPasswordError("");
    return true;
  };

  const handlePasswordSubmit = async () => {
    if (!passwordRequirement || !pendingProfile || isPasswordValidating) return;
    const input = sanitizePassword(passwordInput);
    if (input.length !== 4) {
      setPasswordError("Enter the 4 characters shown on the sticker.");
      return;
    }

    setIsPasswordValidating(true);
    setPasswordError("");

    // Map password requirement type to edge function config key
    const edgeFunctionType: 'vehicleNumber' | 'metroCard' | 'metefyToken' = 
      passwordRequirement.type === "token" ? "metefyToken" 
      : passwordRequirement.type === "metro" ? "metroCard" 
      : "vehicleNumber";

    // Check if protected profile data edge function is enabled for this search type
    if (!edgeFunctions[edgeFunctionType].protectedProfileData) {
      toast({
        title: "Password validation disabled",
        description: "This feature is temporarily disabled by admin.",
        variant: "destructive",
      });
      setIsPasswordValidating(false);
      return;
    }

    // Determine search type for edge function API call
    const searchType = passwordRequirement.type === "token" ? "token" 
      : passwordRequirement.type === "metro" ? "metro" 
      : "vehicle";

    try {
      // Call edge function to validate password and get sensitive data
      const { data: { session } } = await supabase.auth.getSession();
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      
      const response = await fetch(`${supabaseUrl}/functions/v1/get-protected-profile-data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token || ''}`,
        },
        body: JSON.stringify({
          profileId: pendingProfile.id,
          searchType: searchType,
          password: input,
          token: passwordRequirement.type === "token" ? (pendingTokenPassword || "") : null,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to validate password' }));
        if (response.status === 401) {
          setPasswordError("Incorrect password. Try again.");
          setIsPasswordValidating(false);
          return;
        }
        throw new Error(errorData.error || 'Failed to validate password');
      }

      const sensitiveData = await response.json();

      // Merge sensitive data with profile
      const profileWithSensitiveData = {
        ...pendingProfile,
        ...sensitiveData,
      };

      setSearchResult(profileWithSensitiveData);
      setIsDialogOpen(true);
      resetPasswordState();
      setIsPasswordValidating(false);
    } catch (error: any) {
      console.error('Error validating password:', error);
      setPasswordError("Failed to validate password. Please try again.");
      setIsPasswordValidating(false);
    }
  };
  const [stickerPreviewUrl, setStickerPreviewUrl] = useState<string>(stickerShowcaseImages[0]);
  
  const blurActiveElement = () => {
    const active = document.activeElement as HTMLElement | null;
    if (active && typeof active.blur === 'function') {
      active.blur();
    }
  };
  
  const openDemoProfile = (profile: Profile) => {
    // Directly open owner profile with provided demo data
    setPendingDemoProfile(null);
    blurActiveElement();
    presentProfile(profile);
    // Ensure any previous loading dialogs are closed
    setIsStickerDialogOpen(false);
    setIsScanDemoLoading(false);
  };
  
  // First useEffect to handle URL parameters
  useEffect(() => {
    // Handle demo profile parameter first (location-based or legacy true/1)
    const demoParam = searchParams.get('demo');
    if (demoParam) {
      // Show loading skeleton immediately
      setSearchResult(null);
      setIsDialogOpen(true);
      
      // Use demo profile for all demo parameters
      presentProfile(getDemoProfile() as Profile);
      
      // Log the demo search in the background (non-blocking) with IP address
      (async () => {
        try {
          // Get IP address in parallel
          let ipAddress: string | null = null;
          try {
            const ipResponse = await fetch('https://api.ipify.org?format=json');
            if (ipResponse.ok) {
              const ipData = await ipResponse.json();
              ipAddress = ipData.ip || null;
            }
          } catch (ipError) {
            // Silently fail IP fetching - don't block logging
            console.warn('Failed to fetch IP address:', ipError);
          }
          
          await supabase.rpc('log_demo_search', {
            p_location: demoParam.toLowerCase(),
            p_ip_address: ipAddress
          });
        } catch (logError) {
          // Silently fail logging - don't block demo profile opening
          console.error('Failed to log demo search:', logError);
        }
      })();
      
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps

    // Handle general, helmet, and metro parameters (treat as metro card number)
    const generalParam = searchParams.get('general');
    const helmetParam = searchParams.get('helmet');
    const metroParam = searchParams.get('metro');
    const freeParam = searchParams.get('free');

    if (generalParam || helmetParam || metroParam || freeParam) {
      setIsFromUrl(true);
      setSearchType('metroCard');
      setSearchValue(generalParam || helmetParam || metroParam || freeParam || '');
      return;
    }

    const userParam = searchParams.get('user');
    
    // Handle username parameter: /?user=username
    if (userParam) {
      setIsFromUrl(true);
      setSearchType('username');
      setSearchValue(userParam.toLowerCase().trim());
      return;
    }

    const type = searchParams.get('type');
    const number = searchParams.get('number');
    const tokenNumber = searchParams.get('token');
    const shortTokenNumber = searchParams.get('t');
    const ownerNumber = searchParams.get('owner');
    
    // Handle the shorter token formats first
    if (tokenNumber || shortTokenNumber || ownerNumber) {
      setIsFromUrl(true);
      setSearchType('metefyToken');
      setSearchValue(tokenNumber || shortTokenNumber || ownerNumber || '');
      return;
    }
    
    // Handle the traditional type & number format
    if (type) {
      const normalizedType = type.toLowerCase();
      if (normalizedType === 'vehicle' || normalizedType === 'vehiclenumber') {
        setIsFromUrl(true);
        setSearchType('vehicleNumber');
        if (number) {
          setSearchValue(number.toUpperCase());
        }
      } else if (normalizedType === 'metrocard' || normalizedType === 'metefytoken' || normalizedType === 'token') {
        setIsFromUrl(true);
        // Convert to proper case for internal use
        setSearchType(normalizedType === 'token' ? 'metefyToken' : normalizedType === 'metrocard' ? 'metroCard' : 'metefyToken');
        if (number) {
          setSearchValue(number);
        }
      } else {
        toast({
          title: "Invalid Search Type",
          description: "The search type in the URL is invalid.",
          variant: "destructive",
        });
      }
    }
  }, [searchParams]);

  // Second useEffect to handle the search only when values come from URL
  useEffect(() => {
    if (isFromUrl) {
      // Scroll to find owner section first with longer timeout for first load
      setTimeout(() => {
        const element = document.getElementById('find-owner');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        
        // Then perform the search only if we have a search value
        if (searchType && searchValue.trim()) {
          setTimeout(() => {
            handleSearch();
            // Only reset isFromUrl after all actions are complete
            setIsFromUrl(false);
          }, 800);
        } else {
          // If no search value, reset isFromUrl after scroll
          setIsFromUrl(false);
        }
      }, 1000);
    }
  }, [isFromUrl, searchType, searchValue]);

  // Handle QR scan result
  useEffect(() => {
    if (qrScanToken && searchType === 'metefyToken' && searchValue === qrScanToken) {
      // Scroll to find owner section
      setTimeout(() => {
        const element = document.getElementById('find-owner');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        // Perform search after scrolling
        setTimeout(() => {
          handleSearch();
          setQrScanToken(null); // Reset after search
        }, 800);
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qrScanToken, searchType, searchValue]);

  const validateInput = () => {
    if (!searchValue.trim()) {
      toast({
        title: "Missing Information",
        description: "Please enter an identifier to search.",
        variant: "destructive",
      });
      return false;
    }

    if (searchType === "vehicleNumber") {
      const vehicleNumber = searchValue.trim().toUpperCase();
      if (vehicleNumber.length < 6 || vehicleNumber.length > 15) {
        toast({
          title: "Invalid Vehicle Number",
          description: "Vehicle number must be between 6 and 15 characters.",
          variant: "destructive",
        });
        return false;
      }
      if (vehicleNumber !== searchValue) {
        setSearchValue(vehicleNumber);
      }
    } else if (searchType === "metroCard" && (searchValue.length < 4 || searchValue.length > 16)) {
      toast({
        title: "Invalid Metro Card Number",
        description: "Metro card number must be between 4 and 16 digits.",
        variant: "destructive",
      });
      return false;
    }

    if (searchType === "metefyToken") {
      const tokenRegex = /^\d{12}$/;
      if (!tokenRegex.test(searchValue)) {
        toast({
          title: "Invalid Metefy Token",
          description: "Metefy token must be a 12-digit number.",
          variant: "destructive",
        });
        return false;
      }
    }

    if (searchType === "username") {
      const trimmedUsername = searchValue.trim().toLowerCase();
      if (trimmedUsername.length < 3 || trimmedUsername.length > 30) {
        toast({
          title: "Invalid Username",
          description: "Username must be between 3 and 30 characters.",
          variant: "destructive",
        });
        return false;
      }
      if (!/^[a-zA-Z0-9_-]+$/.test(trimmedUsername)) {
        toast({
          title: "Invalid Username",
          description: "Username can only contain letters, numbers, underscores, and hyphens.",
          variant: "destructive",
        });
        return false;
      }
    }

    return true;
  };
  
  const handleSearch = async () => {
    if (!validateInput()) return;

    const normalizedValue =
      searchType === "vehicleNumber" ? searchValue.trim().toUpperCase() 
      : searchType === "username" ? searchValue.trim().toLowerCase()
      : searchValue;
    const searchTypeForApi =
      searchType === "vehicleNumber" ? "vehicle_number" 
      : searchType === "metroCard" ? "metro_card" 
      : searchType === "username" ? "username"
      : "metefy_token";
    
    setIsSearching(true);
    setSearchResult(null);
    setIsDialogOpen(true);
    
    try {
        // Pre-check IP limit first (no logging yet) if enabled for this search type and online
        if (edgeFunctions[searchType].secureSearch && navigator.onLine) {
          try {
            const { error: preErr } = await supabase.functions.invoke('secure-search', {
            body: {
              precheck: true,
              p_owner_id: null,
            p_search_type: searchTypeForApi,
            p_search_value: normalizedValue,
              p_latitude: null,
              p_longitude: null,
              p_city: null,
              p_address: null,
              p_country: null,
              p_region: null,
              p_ip: null
            }
          });
          if (preErr) {
            setIsDialogOpen(false);
            toast({
              title: 'Too many searches',
              description: 'You can perform up to 10 searches per minute from this IP address. Please try again later.',
              variant: 'destructive'
            });
            setIsSearching(false);
            return;
          }
          } catch {
            setIsDialogOpen(false);
            toast({ title: 'Search failed', description: 'Please try again in a moment.', variant: 'destructive' });
            setIsSearching(false);
            return;
          }
        } else if (!navigator.onLine && edgeFunctions[searchType].secureSearch) {
          // Offline - skip rate limit check, will use cache
        }

      let tokenMeta: { token: string; custom_password: string | null } | null = null;
      let userIdForCheck: string | null = null;
      let headingValue: string | null = null;
      let siteLinkValue: string | null = null;

      // For metefy token, first get the user_id and token info
      // Check for token regardless of verification status - if it has info, show it
      // We need to check if custom_password exists to determine if password is needed
      if (searchType === "metefyToken") {
        // Check cache first if offline
        if (!navigator.onLine) {
          const cachedData = queryClient.getQueryData(['profile', searchType, normalizedValue]);
          if (cachedData) {
            const profileData = cachedData as any;
            const profileWithToken: Profile = {
              ...profileData,
              vehicle_number: profileData.vehicle_number ?? null,
              metro_password_mode: (profileData.metro_password_mode ?? "disabled") as PasswordMode,
              metro_card_password: null,
              vehicle_password_mode: (profileData.vehicle_password_mode ?? "disabled") as PasswordMode,
              vehicle_password: null,
              metefy_token: normalizedValue,
              heading: null,
              site_link: null,
              social_sms: profileData.social_sms ?? null,
              social_call: profileData.social_call ?? null,
              social_telegram: profileData.social_telegram ?? null,
              social_whatsapp: profileData.social_whatsapp ?? null,
              use_social_icons: profileData.use_social_icons ?? false,
              show_only_filled_socials: profileData.show_only_filled_socials ?? false
            };
            presentProfile(profileWithToken, "token", { token: normalizedValue, hasPassword: false });
            setIsSearching(false);
            return;
          } else {
            setIsDialogOpen(false);
            toast({
              title: 'Offline',
              description: 'You are offline. Please connect to the internet to search.',
              variant: 'destructive'
            });
            setIsSearching(false);
            return;
          }
        }
        
        const { data: tokenData, error: tokenError } = await supabase
          .from('metefy_tokens')
          .select('user_id, heading, site_link, name, call, whatsapp, x_username, is_verified, custom_password, owner_info, photo_url')
          .eq('token', normalizedValue)
          .maybeSingle();

        if (tokenError) throw tokenError;
        if (!tokenData) {
          // Show demo profile instead of error
          presentProfile(getDemoProfile() as Profile);
          setIsSearching(false);
          return;
        }

        userIdForCheck = tokenData.user_id;
        headingValue = tokenData.heading ?? null;
        siteLinkValue = tokenData.site_link ?? null;
        // Store token and password requirement for later use
        tokenMeta = { 
          token: normalizedValue,
          custom_password: tokenData.custom_password || null
        };

        // If token has no user_id, check if it has info
        if (!tokenData.user_id) {
          // If token has info, create a profile from token info
          if (tokenData.name || tokenData.call || tokenData.whatsapp || tokenData.x_username || tokenData.owner_info || tokenData.photo_url) {
            const nameParts = tokenData.name ? tokenData.name.trim().split(' ') : [];
            const firstName = nameParts[0] || null;
            const lastName = nameParts.slice(1).join(' ') || null;
            
            const profileFromToken: Profile = {
              id: '', // No user ID
              first_name: firstName,
              last_name: lastName,
              x_username: tokenData.x_username,
              bio: tokenData.owner_info || null,
              mask_bio: false,
              metro_card_number: null,
              vehicle_number: null,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              show_email: false,
              show_mobile: false,
              metefy_token: normalizedValue,
              photo_url: tokenData.photo_url || null,
              social_sms: null,
              social_call: tokenData.call,
              social_telegram: null,
              social_whatsapp: tokenData.whatsapp,
              use_social_icons: true,
              heading: headingValue,
              site_link: siteLinkValue,
              metro_password_mode: "disabled",
              metro_card_password: null,
              vehicle_password_mode: "disabled",
              vehicle_password: null
            };
            
            presentProfile(profileFromToken, "token", { token: normalizedValue, hasPassword: false });
            setIsSearching(false);
            return;
      } else {
            // Token exists but has no info and no user - show demo profile
            presentProfile(getDemoProfile() as Profile);
            setIsSearching(false);
            return;
      }
        }
      }

      // Build select query - Optimize by including social fields when we know password is not needed
      // For token searches, we already know if password is needed from tokenMeta
      // For vehicle/metro, we'll fetch profile first, then conditionally fetch social fields
      const baseFields = `
        id,
        first_name,
        last_name,
        x_username,
        bio,
        mask_bio,
        metro_card_number,
        vehicle_number,
        metro_password_mode,
        vehicle_password_mode,
        created_at,
        updated_at,
        show_email,
        show_mobile,
        photo_url,
        use_social_icons,
        show_only_filled_socials
      `;
      
      // For token searches: include social fields if no password is needed (we know this from tokenMeta)
      // For vehicle/metro: don't include yet - we'll check password_mode after fetching
      const canIncludeSocialFields = searchType === "metefyToken" 
        ? !tokenMeta?.custom_password // Token has no password - safe to include
        : false; // For vehicle/metro, check password_mode first
      
      // Include social fields only if we know it's safe (no password protection)
      const socialFields = canIncludeSocialFields ? `,
        social_sms,
        social_call,
        social_telegram,
        social_whatsapp
      ` : '';
      
      const selectFields = (baseFields + socialFields).trim();

      // Create query key for caching
      const queryKey = ['profile', searchType, normalizedValue || userIdForCheck];
      
      // Check cache first - if found and fresh, use it
      const cachedData = queryClient.getQueryData(queryKey);
      let data: any = null;
      let error: any = null;

      if (cachedData) {
        // Use cached data
        data = cachedData;
      } else {
        // Check if offline - if offline and no cache, show error
        if (!navigator.onLine) {
          setIsDialogOpen(false);
          toast({
            title: 'Offline',
            description: 'You are offline. Please connect to the internet to search.',
            variant: 'destructive'
          });
          setIsSearching(false);
          return;
        }
        
        // Fetch from database and cache it
        let query = supabase.from('profiles').select(selectFields);

        if (searchType === "vehicleNumber") {
          query = query.eq('vehicle_number', normalizedValue);
        } else if (searchType === "metroCard") {
          query = query.eq('metro_card_number', normalizedValue);
        } else if (searchType === "username") {
          query = query.eq('username', normalizedValue);
        } else {
          query = query.eq('id', userIdForCheck!);
        }

        const result = await query.maybeSingle();
        error = result.error;
        data = result.data;
        
        // Cache the result if successful
        // Note: staleTime and gcTime are already set in QueryClient defaults in main.tsx
        if (data && !error) {
          queryClient.setQueryData(queryKey, data);
        }
      }
      
      if (error) throw error;
      
      if (data) {
        // Type assertion: data is guaranteed to be Profile type after null check
        const profileData = data as any;
        
        // Use the searched token if searching by token, otherwise use first token
        let searchedToken: string | null = null;
      if (searchType === "metefyToken") {
        searchedToken = normalizedValue;
      } else {
        // Check if offline - skip token fetch if offline (will use null token)
        if (navigator.onLine) {
          try {
            const { data: metefyDataArray } = await supabase
              .from('metefy_tokens')
              .select('token, heading, site_link')
              .eq('user_id', profileData.id)
              .eq('is_verified', true)
              .limit(1);
            const firstToken = metefyDataArray && metefyDataArray.length > 0 ? metefyDataArray[0] : null;
            searchedToken = firstToken?.token ?? null;
            headingValue = firstToken?.heading ?? headingValue;
            siteLinkValue = firstToken?.site_link ?? siteLinkValue;
          } catch (error) {
            // If offline or fetch fails, continue without token
            console.log('Could not fetch token (offline or error):', error);
          }
        }
        // If offline, token will be null - that's okay, profile will still display
      }

        const profileWithToken: Profile = {
          ...profileData,
          vehicle_number: profileData.vehicle_number ?? null,
          metro_password_mode: (profileData.metro_password_mode ?? "disabled") as PasswordMode,
          metro_card_password: null, // Not fetched - will be validated server-side
          vehicle_password_mode: (profileData.vehicle_password_mode ?? "disabled") as PasswordMode,
          vehicle_password: null, // Not fetched - will be validated server-side
          metefy_token: searchedToken,
          heading: headingValue,
          site_link: siteLinkValue,
          // Social fields are already included if no password needed (for tokens) or if password_mode is disabled (for vehicle/metro)
          social_sms: profileData.social_sms ?? null,
          social_call: profileData.social_call ?? null,
          social_telegram: profileData.social_telegram ?? null,
          social_whatsapp: profileData.social_whatsapp ?? null,
          use_social_icons: profileData.use_social_icons ?? false,
          show_only_filled_socials: profileData.show_only_filled_socials ?? false
        };

        // Check if password is needed
        const needsPassword = searchType === "metefyToken" 
          ? (tokenMeta?.custom_password !== null && tokenMeta?.custom_password !== undefined && tokenMeta?.custom_password !== '') // Only require password if custom_password exists
          : searchType === "vehicleNumber"
            ? profileData.vehicle_password_mode !== "disabled"
            : searchType === "metroCard"
              ? profileData.metro_password_mode !== "disabled"
            : searchType === "username"
              ? false // Username searches don't require password
              : false;

        // Social fields handling:
        // - For token searches without password: already included in query
        // - For vehicle/metro without password: fetch directly (faster than edge function)
        // - For password-protected profiles: will be fetched after password validation via edge function
        if (!needsPassword && profileData.use_social_icons && !canIncludeSocialFields) {
          // Vehicle/metro search without password - fetch social fields directly (no edge function needed)
          try {
            const { data: socialData, error: socialError } = await supabase
              .from('profiles')
              .select('social_sms, social_call, social_telegram, social_whatsapp')
              .eq('id', profileData.id)
              .single();
            
            if (!socialError && socialData) {
              Object.assign(profileWithToken, {
                social_sms: socialData.social_sms ?? null,
                social_call: socialData.social_call ?? null,
                social_telegram: socialData.social_telegram ?? null,
                social_whatsapp: socialData.social_whatsapp ?? null,
              });
            }
          } catch (error) {
            console.error('Error fetching social fields:', error);
            // Continue without social fields
          }
        }
        
        if (searchType === "metefyToken") {
          const hasPassword = (tokenMeta?.custom_password !== null && tokenMeta?.custom_password !== undefined && tokenMeta?.custom_password !== '');
          presentProfile(profileWithToken, "token", { token: normalizedValue, hasPassword });
        } else if (searchType === "username") {
          presentProfile(profileWithToken, null); // Username searches don't require password
        } else {
          const passwordContext =
            searchType === "vehicleNumber" ? "vehicle" : searchType === "metroCard" ? "metro" : null;
          presentProfile(profileWithToken, passwordContext);
        }

        // Log the search
          (async () => {
            try {
              // Get IP address for unique search tracking
              const { getClientIP } = await import('@/utils/getIP');
              const ipAddress = await getClientIP();
              
              await supabase.rpc('log_search_direct', {
                p_owner_id: profileData.id,
              p_search_type: searchTypeForApi,
              p_search_value: normalizedValue,
                p_latitude: null,
                p_longitude: null,
                p_city: null,
                p_address: null,
                p_country: null,
                p_region: null,
                p_ip_address: ipAddress,
              });
            } catch {}
          })();
        
        // After profile is visible, enrich the last search with approximate location (fire-and-forget)
        if (edgeFunctions[searchType].getLocation) {
          (async () => {
            try {
              const locPromise = getFinderLocation();
              const timeout = new Promise((resolve) => setTimeout(() => resolve(null), 2000));
              const location: any = (await Promise.race([locPromise, timeout])) as any;
              if (!location) return;
              await supabase.rpc('update_last_search_location', {
              p_owner_id: profileData.id,
              p_search_type: searchTypeForApi,
              p_search_value: normalizedValue,
              p_latitude: location.latitude ?? null,
              p_longitude: location.longitude ?? null,
              p_city: location.city ?? null,
              p_address: location.address ?? null,
              p_country: location.country ?? null,
              p_region: location.region ?? null,
            });
            } catch {}
          })();
        }
      } else {
        // Show demo profile instead of error
        presentProfile(getDemoProfile() as Profile);
      }
    } catch (error) {
      toast({
        title: "Search Failed",
        description: "There was an error performing your search.",
        variant: "destructive",
      });
    } finally {
      setIsSearching(false);
    }
  };
  
  const copyTweetTemplate = () => {
    if (!searchResult) return;
    
    const tweetText = `Hey @mete_fy, I found a lost product of @${searchResult.x_username || 'user'} from the public profile having metro card number ${searchResult.metro_card_number}`;
    
    navigator.clipboard.writeText(tweetText);
    toast({
      title: "Copied!",
      description: "Tweet template has been copied to clipboard.",
    });
  };
  
  return (
    <>
      <Helmet>
        <title>Metefy – Link Your Belongings to You | The Smart Lost & Found Network</title>
        <meta name="description" content="Give your metro card and various other items a digital identity with Metefy. Anyone who finds them can contact you instantly." />
        <meta name="keywords" content="lost and found platform, smart lost and found, digital ID for belongings, metefy stickers, return lost items, metro card identity, contact item owner, product identity stickers, p2p lost and found system, track lost items, finder return contact, item tagging solution, Metefy return platform" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="googlebot" content="index, follow" />
        <meta name="author" content="Metefy" />
        <meta name="language" content="English" />
        <meta name="revisit-after" content="7 days" />
        <link rel="canonical" href="https://www.metefy.com/" />
        <link rel="preconnect" href="https://res.cloudinary.com" />
        <link rel="dns-prefetch" href="https://res.cloudinary.com" />
        <meta property="og:title" content="Metefy – Link Your Belongings to You | The Smart Lost & Found Network" />
        <meta property="og:description" content="Give your metro card and various other items a digital identity with Metefy. Anyone who finds them can contact you instantly." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.metefy.com/" />
        <meta property="og:image" content="https://www.metefy.com/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="Metefy" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Metefy – Link Your Belongings to You | The Smart Lost & Found Network" />
        <meta name="twitter:description" content="Give your metro card and various other items a digital identity with Metefy. Anyone who finds them can contact you instantly." />
        <meta name="twitter:image" content="https://www.metefy.com/og-image.png" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "Metefy",
            "url": "https://www.metefy.com/",
            "description": "Give your metro card and various other items a digital identity with Metefy. Anyone who finds them can contact you instantly.",
            "potentialAction": {
              "@type": "SearchAction",
              "target": "https://www.metefy.com/search?q={search_term_string}",
              "query-input": "required name=search_term_string"
            }
          })}
        </script>
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Metefy",
            "url": "https://www.metefy.com/",
            "logo": "https://www.metefy.com/logo.png",
            "description": "Give your metro card and various other items a digital identity with Metefy. Anyone who finds them can contact you instantly.",
            "sameAs": [
              "https://x.com/mete_fy",
              "https://instagram.com/mete_fy",
              "https://www.facebook.com/profile.php?id=61575578170519",
              "https://www.pinterest.com/mete_fy"
            ],
            "contactPoint": {
              "@type": "ContactPoint",
              "contactType": "Customer Service",
              "availableLanguage": ["English"]
            }
          })}
        </script>
      </Helmet>
      <div className="min-h-screen bg-background">
        <AboutHeader 
          user={user} 
          onQrScanResult={(token) => {
            // Set search type to metefy token and value
            setSearchType('metefyToken');
            setSearchValue(token);
            // Set QR scan token to trigger useEffect
            setQrScanToken(token);
          }}
          openDemoProfile={openDemoProfile}
        />
        
        <main className={`flex-1 ${user ? 'pb-24 md:pb-0' : ''}`} role="main">
          <article>
            <AboutHeroSection 
              searchType={searchType}
              setSearchType={setSearchType}
              searchValue={searchValue}
              setSearchValue={setSearchValue}
              handleSearch={handleSearch}
              openDemoProfile={openDemoProfile}
            />
            
            <AboutFindOwnerSection
              searchType={searchType}
              setSearchType={setSearchType}
              searchValue={searchValue}
              setSearchValue={setSearchValue}
              handleSearch={handleSearch}
              isSearching={isSearching}
              searchResult={searchResult}
              isDialogOpen={isDialogOpen}
              setIsDialogOpen={setIsDialogOpen}
              pageKey="index"
              mode="metro"
              openDemoProfile={openDemoProfile}
            />
            
            <AboutProblemSolutionSection />
            
            <div id="features">
              <AboutFeaturesSection />
            </div>
            
            <div id="how-it-works">
              <AboutHowItWorksSection openDemoProfile={openDemoProfile} />
            </div>
            
            <AboutUseCasesSection />
            
            <AboutSocialProofSection />
            
            <div id="teams">
              <AboutTeamSection />
            </div>
            
            <AboutBlogSection />
            
            <AboutCtaSection openDemoProfile={openDemoProfile} />
            
            <div id="faq">
              <AboutFaqSection />
            </div>
            
            <AboutFooter />
          </article>
        </main>
        {user && <MobileBottomNav />}

        {/**
         * Step 1: Metefy QR Sticker preview dialog (disabled per request)
         *
         * <AlertDialog open={isStickerDialogOpen} onOpenChange={setIsStickerDialogOpen}>
         *   <AlertDialogContent className="bg-white border-none w-[95vw] max-w-sm fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 max-h-[90vh] overflow-y-auto rounded-2xl sm:max-w-md">
         *     <AlertDialogHeader>
         *       <AlertDialogTitle className="text-2xl font-normal text-gray-900 text-center w-full">Buy Metefy Qr Stickers</AlertDialogTitle>
         *       <AlertDialogDescription asChild>
         *         <div className="w-full mt-3 space-y-4">
         *           <img
         *             src={stickerPreviewUrl}
         *             alt="Metefy Sticker"
         *             className="w-full h-auto rounded-lg"
         *           />
         *           <p className="text-center text-gray-700">Apply them on your metro card, phone, laptops, etc.</p>
         *         </div>
         *       </AlertDialogDescription>
         *     </AlertDialogHeader>
         *   </AlertDialogContent>
         * </AlertDialog>
         */}

        {/**
         * Step 2: Scan Demo Loading Dialog (disabled per request)
         *
         * <AlertDialog open={isScanDemoLoading} onOpenChange={setIsScanDemoLoading}>
         *   <AlertDialogContent className="bg-white border-none w-[95vw] max-w-sm fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 max-h-[90vh] overflow-y-auto rounded-2xl sm:max-w-md">
         *     <AlertDialogHeader>
         *       <AlertDialogTitle className="text-center">Your forget your product somewhere</AlertDialogTitle>
         *       <AlertDialogDescription asChild>
         *         <div className="flex flex-col items-center justify-center gap-4 py-4">
         *           <Scan className="w-16 h-16 text-sky-700" />
         *           <p className="text-gray-700 text-center">Your profile on Metefy will show up when finder scans the QR Code</p>
         *         </div>
         *       </AlertDialogDescription>
         *     </AlertDialogHeader>
         *   </AlertDialogContent>
         * </AlertDialog>
         */}
        
        <AboutOwnerProfile 
          isOpen={isDialogOpen}
          setIsOpen={setIsDialogOpen}
          profile={searchResult}
          copyTweetTemplate={copyTweetTemplate}
          isDemo={Boolean(searchResult?.id === 'demo-amit')}
          passwordRequirement={passwordRequirement}
          passwordValue={passwordInput}
          passwordError={passwordError}
          isPasswordValidating={isPasswordValidating}
          onPasswordChange={setPasswordInput}
          onPasswordSubmit={handlePasswordSubmit}
          onPasswordCancel={handlePasswordCancel}
        />
        <FloatingAboutDock onOpenThemeDialog={() => setIsThemeDialogOpen(true)} />
        <ThemeDialog isOpen={isThemeDialogOpen} setIsOpen={setIsThemeDialogOpen} />
      </div>
    </>
  );
}
